import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../Constants';
import { AuthContext } from "../context/AuthContext";
import {
  FiZap, FiFilter, FiList, FiPlay, FiActivity, FiPlus, FiTrash2, FiEdit2, FiX,
  FiToggleLeft, FiToggleRight, FiChevronDown, FiChevronUp, FiCopy, FiAlertCircle,
  FiCheckCircle, FiXCircle, FiClock, FiArrowUp, FiArrowDown, FiMail, FiMessageSquare,
  FiBell, FiGlobe, FiSend, FiRefreshCw, FiSearch, FiSave, FiPlusCircle, FiInfo,
  FiChevronLeft, FiChevronRight, FiSliders
} from 'react-icons/fi';
import { toast } from 'react-toastify';

// ============================================================================
// Static config — mirrors the Automation module enums 1:1 with the backend.
// ============================================================================

const TRIGGER_OPTIONS = [
  { value: 'LEAD_CREATED', label: 'New lead created' },
  { value: 'LEAD_STATUS_CHANGED', label: 'Lead status changed' },
  { value: 'LEAD_PROJECT_CHANGED', label: 'Lead project changed' },
  { value: 'LEAD_ASSIGNED', label: 'Lead assigned to user' },
  { value: 'LEAD_REPEAT_SUBMISSION', label: 'Lead submitted again' },
  { value: 'LEAD_CONVERTED', label: 'Lead converted' },
  { value: 'APPOINTMENT_CREATED', label: 'Appointment created' },
  { value: 'APPOINTMENT_RESCHEDULED', label: 'Appointment rescheduled' },
];

const ACTION_TYPE_OPTIONS = [
  { value: 'EMAIL', label: 'Send email', icon: FiMail },
  { value: 'SMS', label: 'Send SMS', icon: FiMessageSquare },
  { value: 'WHATSAPP', label: 'Send WhatsApp message', icon: FiSend },
  { value: 'CUSTOM_API', label: 'Call a webhook / custom API', icon: FiGlobe },
  { value: 'INTERNAL_NOTIFICATION', label: 'Notify a user in-app', icon: FiBell },
];

const OPERATOR_OPTIONS = [
  { value: 'EQUALS', label: 'Equals', needsValue: true },
  { value: 'NOT_EQUALS', label: 'Does not equal', needsValue: true },
  { value: 'CONTAINS', label: 'Contains', needsValue: true },
  { value: 'NOT_CONTAINS', label: 'Does not contain', needsValue: true },
  { value: 'IS_EMPTY', label: 'Is empty', needsValue: false },
  { value: 'IS_NOT_EMPTY', label: 'Is not empty', needsValue: false },
];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// Mirrors CustomApiWorkflowActionExecutor's bodyType switch on the backend.
const BODY_TYPE_OPTIONS = [
  { value: 'JSON', label: 'JSON' },
  { value: 'FORM_URLENCODED', label: 'Form URL Encoded' },
  { value: 'MULTIPART_FORM_DATA', label: 'Multipart Form Data' },
  { value: 'TEXT', label: 'Raw Text' },
  { value: 'XML', label: 'XML' },
  { value: 'NONE', label: 'No Body' },
];

const LEAD_VARIABLES = [
  'lead.leadId', 'lead.name', 'lead.phone', 'lead.otherContactNo', 'lead.email',
  'lead.companyName', 'lead.city', 'lead.state', 'lead.address', 'lead.comment',
  'lead.reference', 'lead.projectName', 'lead.sourceName', 'lead.assignedToId',
  'lead.assignedToName', 'lead.createdByName', 'lead.companyId', 'lead.statusName',
];

const EVENT_VARIABLES_BY_TRIGGER = {
  LEAD_REPEAT_SUBMISSION: ['event.ingestionLabel', 'event.repeatCount'],
  LEAD_STATUS_CHANGED: ['event.oldStatus', 'event.newStatus'],
  LEAD_PROJECT_CHANGED: ['event.oldProject', 'event.newProject'],
  LEAD_ASSIGNED: ['event.assignedToId', 'event.assignedToName'],
  LEAD_CONVERTED: ['event.convertedById', 'event.convertedByName'],
  APPOINTMENT_CREATED: ['event.preferredDate', 'event.preferredTime', 'event.personName', 'event.departmentName'],
  APPOINTMENT_RESCHEDULED: ['event.preferredDate', 'event.preferredTime'],
  LEAD_CREATED: [],
};

const emptyCondition = () => ({ field: 'lead.sourceName', operator: 'EQUALS', value: '' });
const emptyAction = (order) => ({
  actionType: 'EMAIL',
  order,
  to: '',
  subject: '',
  message: '',
  templateId: '',
  httpMethod: 'POST',
  url: '',
  headers: {},
  body: '',
  bodyType: 'JSON',
  formFields: {},
  queryParams: {},
});

const triggerLabel = (v) => TRIGGER_OPTIONS.find((t) => t.value === v)?.label || v;
const actionLabel = (v) => ACTION_TYPE_OPTIONS.find((t) => t.value === v)?.label || v;
const actionIcon = (v) => ACTION_TYPE_OPTIONS.find((t) => t.value === v)?.icon || FiZap;

const inputClass = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white transition-all duration-200 outline-none hover:border-[#673AB7] focus:border-[#673AB7] focus:ring-1 focus:ring-[#673AB7] text-[13px]";
const smallBtn = "px-2.5 py-1.5 text-xs font-medium rounded-lg border transition flex items-center gap-1.5";

// ============================================================================
// Small shared pieces
// ============================================================================

const StatusBadge = ({ status }) => {
  if (status === 'SUCCESS') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
        <FiCheckCircle size={11} /> Success
      </span>
    );
  }
  if (status === 'FAILED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
        <FiXCircle size={11} /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
      {status || 'Unknown'}
    </span>
  );
};

const VariablePicker = ({ triggerType }) => {
  const [open, setOpen] = useState(false);
  const vars = useMemo(() => [...LEAD_VARIABLES, ...(EVENT_VARIABLES_BY_TRIGGER[triggerType] || [])], [triggerType]);
  const copy = (v) => {
    navigator.clipboard.writeText(`{{${v}}}`);
    toast.success(`Copied {{${v}}} — paste it into a field`);
  };
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] font-medium text-[#673AB7] hover:bg-[#F3E8FF] dark:hover:bg-purple-900/20 px-2 py-1 rounded-md transition flex items-center gap-1"
      >
        <FiCopy size={11} /> Insert variable {open ? <FiChevronUp size={11} /> : <FiChevronDown size={11} />}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-64 max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-1.5">
          {vars.map((v) => (
            <button
              type="button"
              key={v}
              onClick={() => { copy(v); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 rounded-md text-[12px] font-mono text-gray-700 dark:text-gray-200 hover:bg-[#F3E8FF] dark:hover:bg-purple-900/20 hover:text-[#673AB7] transition"
            >
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Conditions editor
// ============================================================================

const ConditionsEditor = ({ conditions, onChange }) => {
  const update = (idx, patch) => onChange(conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const remove = (idx) => onChange(conditions.filter((_, i) => i !== idx));
  const add = () => onChange([...conditions, emptyCondition()]);

  return (
    <div>
      {conditions.length === 0 && (
        <p className="text-[12px] text-gray-500 dark:text-gray-400 italic mb-2">
          No conditions — this rule will run every time the trigger fires.
        </p>
      )}
      <div className="space-y-2">
        {conditions.map((cond, idx) => {
          const op = OPERATOR_OPTIONS.find((o) => o.value === cond.operator);
          return (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5">
              <input
                className={`${inputClass} sm:col-span-4 font-mono`}
                placeholder="lead.statusName"
                value={cond.field}
                onChange={(e) => update(idx, { field: e.target.value })}
                list="condition-field-suggestions"
              />
              <select
                className={`${inputClass} sm:col-span-3`}
                value={cond.operator}
                onChange={(e) => {
                  const nextOp = OPERATOR_OPTIONS.find((o) => o.value === e.target.value);
                  update(idx, { operator: e.target.value, value: nextOp?.needsValue ? cond.value : '' });
                }}
              >
                {OPERATOR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {op?.needsValue ? (
                <input
                  className={`${inputClass} sm:col-span-4`}
                  placeholder="Value to compare"
                  value={cond.value}
                  onChange={(e) => update(idx, { value: e.target.value })}
                />
              ) : (
                <div className="sm:col-span-4 text-[12px] text-gray-400 italic px-1">No value needed</div>
              )}
              <button
                type="button"
                onClick={() => remove(idx)}
                className="sm:col-span-1 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition justify-self-end"
                title="Remove condition"
              >
                <FiTrash2 size={15} />
              </button>
            </div>
          );
        })}
      </div>
      <datalist id="condition-field-suggestions">
        {LEAD_VARIABLES.map((v) => <option key={v} value={v} />)}
      </datalist>
      <button type="button" onClick={add} className={`${smallBtn} mt-2.5 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-[#673AB7] hover:text-[#673AB7] text-gray-600 dark:text-gray-300`}>
        <FiPlus size={13} /> Add condition
      </button>
      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
        <FiInfo size={11} /> All conditions must be true (AND). Comparisons are case-insensitive text matches only.
      </p>
    </div>
  );
};

// ============================================================================
// Generic key/value editor — reused for headers, query params, and form fields
// ============================================================================

const KeyValueEditor = ({ rows: rowsObj, onChange, keyPlaceholder, valuePlaceholder, addLabel, hint }) => {
  const rows = Object.entries(rowsObj || {});
  const setRow = (idx, key, value) => {
    const next = [...rows];
    next[idx] = [key, value];
    onChange(Object.fromEntries(next));
  };
  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(Object.fromEntries(next));
  };
  const addRow = () => onChange(Object.fromEntries([...rows, ['', '']]));

  return (
    <div>
      {rows.map(([k, v], idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <input className={`${inputClass} font-mono`} placeholder={keyPlaceholder} value={k} onChange={(e) => setRow(idx, e.target.value, v)} />
          <input className={`${inputClass} font-mono`} placeholder={valuePlaceholder} value={v} onChange={(e) => setRow(idx, k, e.target.value)} />
          <button type="button" onClick={() => removeRow(idx)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition">
            <FiTrash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow} className={`${smallBtn} bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-[#673AB7] hover:text-[#673AB7] text-gray-600 dark:text-gray-300`}>
        <FiPlus size={12} /> {addLabel}
      </button>
      {hint && <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
};

// ============================================================================
// Single action's type-specific fields
// ============================================================================

const ActionFields = ({ action, onChange, triggerType, whatsappConfigured }) => {
  const set = (patch) => onChange({ ...action, ...patch });

  if (action.actionType === 'EMAIL' || action.actionType === 'SMS') {
    const isEmail = action.actionType === 'EMAIL';
    const usingTemplate = !!action.templateId;
    return (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">To</label>
          <VariablePicker triggerType={triggerType} />
        </div>
        <input
          className={inputClass}
          placeholder={isEmail ? "Defaults to lead's email" : "Defaults to lead's phone"}
          value={action.to || ''}
          onChange={(e) => set({ to: e.target.value })}
        />
        <label className="flex items-center gap-2 text-[12px] text-gray-600 dark:text-gray-300 select-none cursor-pointer">
          <input
            type="checkbox"
            className="rounded text-[#673AB7] focus:ring-[#673AB7]"
            checked={usingTemplate}
            onChange={(e) => set({ templateId: e.target.checked ? (action.templateId || '') : '' })}
          />
          Use a saved template instead of writing the message here
        </label>
        {usingTemplate ? (
          <input
            className={inputClass}
            placeholder="Template ID"
            value={action.templateId || ''}
            onChange={(e) => set({ templateId: e.target.value })}
          />
        ) : (
          <>
            {isEmail && (
              <input
                className={inputClass}
                placeholder="Subject — e.g. Welcome, {{lead.name}}!"
                value={action.subject || ''}
                onChange={(e) => set({ subject: e.target.value })}
              />
            )}
            <textarea
              className={`${inputClass} min-h-[80px] resize-y`}
              placeholder={isEmail ? "Email body" : "SMS text"}
              value={action.message || ''}
              onChange={(e) => set({ message: e.target.value })}
            />
          </>
        )}
      </div>
    );
  }

  if (action.actionType === 'WHATSAPP') {
    return (
      <div className="space-y-2.5">
        {whatsappConfigured === false && (
          <div className="p-2.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-[12px] text-yellow-800 dark:text-yellow-300 flex items-start gap-2">
            <FiAlertCircle className="mt-0.5 flex-shrink-0" size={14} />
            <span>No WhatsApp integration is configured for this company yet — this action will fail at runtime until that's set up.</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">To</label>
          <VariablePicker triggerType={triggerType} />
        </div>
        <input
          className={inputClass}
          placeholder="Defaults to lead's phone"
          value={action.to || ''}
          onChange={(e) => set({ to: e.target.value })}
        />
        <textarea
          className={`${inputClass} min-h-[80px] resize-y`}
          placeholder="WhatsApp message"
          value={action.message || ''}
          onChange={(e) => set({ message: e.target.value })}
        />
      </div>
    );
  }

  if (action.actionType === 'CUSTOM_API') {
    const method = action.httpMethod || 'POST';
    // Matches CustomApiWorkflowActionExecutor#supportsBody on the backend — every
    // method except GET can carry a body; GET only carries query params.
    const bodyAllowed = method !== 'GET';
    const bodyType = action.bodyType || 'JSON';
    const usesFormFields = bodyType === 'FORM_URLENCODED' || bodyType === 'MULTIPART_FORM_DATA';
    const usesRawBody = bodyAllowed && !usesFormFields && bodyType !== 'NONE';

    return (
      <div className="space-y-2.5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select className={inputClass} value={method} onChange={(e) => set({ httpMethod: e.target.value })}>
            {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="sm:col-span-3 relative">
            <input
              className={`${inputClass} font-mono ${!action.url ? 'border-red-300 dark:border-red-700' : ''}`}
              placeholder="https://example.com/webhook (required)"
              value={action.url || ''}
              onChange={(e) => set({ url: e.target.value })}
            />
          </div>
        </div>
        {!action.url && (
          <p className="text-[11px] text-red-500 flex items-center gap-1"><FiAlertCircle size={11} /> A URL is required for this action to save.</p>
        )}

        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Query params</label>
        </div>
        <KeyValueEditor
          rows={action.queryParams || {}}
          onChange={(qp) => set({ queryParams: qp })}
          keyPlaceholder="param"
          valuePlaceholder="value or {{lead.leadId}}"
          addLabel="Add query param"
          hint="Appended to the URL for every method, including GET."
        />

        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Headers</label>
          <VariablePicker triggerType={triggerType} />
        </div>
        <KeyValueEditor
          rows={action.headers || {}}
          onChange={(h) => set({ headers: h })}
          keyPlaceholder="Header-Name"
          valuePlaceholder="value or {{lead.name}}"
          addLabel="Add header"
          hint="Leave Content-Type unset and it will be set automatically based on the body type below."
        />

        {bodyAllowed ? (
          <>
            <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Body type</label>
            <select className={inputClass} value={bodyType} onChange={(e) => set({ bodyType: e.target.value })}>
              {BODY_TYPE_OPTIONS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>

            {usesFormFields && (
              <>
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Form fields</label>
                  <VariablePicker triggerType={triggerType} />
                </div>
                <KeyValueEditor
                  rows={action.formFields || {}}
                  onChange={(f) => set({ formFields: f })}
                  keyPlaceholder="field"
                  valuePlaceholder="value or {{lead.name}}"
                  addLabel="Add field"
                  hint={bodyType === 'MULTIPART_FORM_DATA'
                    ? 'Sent as multipart/form-data — the boundary is generated and set automatically, no need to configure it.'
                    : 'Sent as application/x-www-form-urlencoded.'}
                />
              </>
            )}

            {usesRawBody && (
              <>
                <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Body</label>
                <textarea
                  className={`${inputClass} min-h-[90px] resize-y font-mono`}
                  placeholder={bodyType === 'XML' ? '<lead><name>{{lead.name}}</name></lead>' : bodyType === 'TEXT' ? 'Raw text body' : '{"leadName": "{{lead.name}}"}'}
                  value={action.body || ''}
                  onChange={(e) => set({ body: e.target.value })}
                />
              </>
            )}

            {bodyType === 'NONE' && (
              <p className="text-[11px] text-gray-400 italic">No body will be sent with this request.</p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-gray-400 italic">Body is not sent for {method} requests — use query params above instead.</p>
        )}
      </div>
    );
  }

  if (action.actionType === 'INTERNAL_NOTIFICATION') {
    return (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-gray-600 dark:text-gray-300">Notify (user ID)</label>
          <VariablePicker triggerType={triggerType} />
        </div>
        <input
          className={inputClass}
          placeholder="Defaults to the lead's currently assigned user — or use {{lead.assignedToId}}"
          value={action.to || ''}
          onChange={(e) => set({ to: e.target.value })}
        />
        <textarea
          className={`${inputClass} min-h-[70px] resize-y ${!action.message ? 'border-red-300 dark:border-red-700' : ''}`}
          placeholder="Notification message (required)"
          value={action.message || ''}
          onChange={(e) => set({ message: e.target.value })}
        />
        {!action.message && (
          <p className="text-[11px] text-red-500 flex items-center gap-1"><FiAlertCircle size={11} /> A message is required.</p>
        )}
      </div>
    );
  }

  return null;
};

// ============================================================================
// Actions editor (reorderable list)
// ============================================================================

const ActionsEditor = ({ actions, onChange, triggerType, whatsappConfigured }) => {
  const sorted = [...actions].sort((a, b) => a.order - b.order);

  const commit = (list) => onChange(list.map((a, i) => ({ ...a, order: i + 1 })));

  const update = (idx, next) => commit(sorted.map((a, i) => (i === idx ? next : a)));
  const remove = (idx) => commit(sorted.filter((_, i) => i !== idx));
  const add = () => commit([...sorted, emptyAction(sorted.length + 1)]);
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[target]] = [next[target], next[idx]];
    commit(next);
  };

  return (
    <div className="space-y-3">
      {sorted.length === 0 && (
        <p className="text-[12px] text-red-500 flex items-center gap-1.5">
          <FiAlertCircle size={13} /> A rule needs at least one action, or it will never do anything.
        </p>
      )}
      {sorted.map((action, idx) => {
        const Icon = actionIcon(action.actionType);
        return (
          <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-900/60 px-3 py-2.5 flex items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <span className="w-5 h-5 rounded-full bg-[#673AB7] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                <Icon className="text-[#673AB7] flex-shrink-0" size={15} />
                <select
                  value={action.actionType}
                  onChange={(e) => update(idx, { ...emptyAction(action.order), actionType: e.target.value })}
                  className="text-[13px] font-semibold bg-transparent outline-none text-gray-800 dark:text-gray-100 cursor-pointer"
                >
                  {ACTION_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 text-gray-400 hover:text-[#673AB7] disabled:opacity-30 disabled:hover:text-gray-400 rounded-md transition" title="Move up">
                  <FiArrowUp size={14} />
                </button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === sorted.length - 1} className="p-1.5 text-gray-400 hover:text-[#673AB7] disabled:opacity-30 disabled:hover:text-gray-400 rounded-md transition" title="Move down">
                  <FiArrowDown size={14} />
                </button>
                <button type="button" onClick={() => remove(idx)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="Remove action">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
            <div className="p-3.5 bg-white dark:bg-gray-800">
              <ActionFields action={action} onChange={(a) => update(idx, a)} triggerType={triggerType} whatsappConfigured={whatsappConfigured} />
            </div>
          </div>
        );
      })}
      <button type="button" onClick={add} className={`${smallBtn} bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-[#673AB7] hover:text-[#673AB7] text-gray-600 dark:text-gray-300`}>
        <FiPlus size={13} /> Add action
      </button>
    </div>
  );
};

// ============================================================================
// Test-fire result table
// ============================================================================

const TestResultsTable = ({ logs }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mt-3">
    <table className="w-full text-left text-[12px]">
      <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
        <tr>
          <th className="p-2.5 font-semibold">#</th>
          <th className="p-2.5 font-semibold">Action</th>
          <th className="p-2.5 font-semibold">Status</th>
          <th className="p-2.5 font-semibold">Detail</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800">
        {logs.map((l) => (
          <tr key={l.id}>
            <td className="p-2.5">{l.actionOrder}</td>
            <td className="p-2.5">{actionLabel(l.actionType)}</td>
            <td className="p-2.5"><StatusBadge status={l.status} /></td>
            <td className="p-2.5 text-gray-500 dark:text-gray-400">{l.detail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ============================================================================
// Rule Builder (create / edit)
// ============================================================================

const RuleBuilder = ({ initialRule, companyId, user, onClose, onSaved, whatsappConfigured }) => {
  const isEdit = !!initialRule?.id;
  const [name, setName] = useState(initialRule?.name || '');
  const [description, setDescription] = useState(initialRule?.description || '');
  const [triggerType, setTriggerType] = useState(initialRule?.triggerType || 'LEAD_CREATED');
  const [active, setActive] = useState(initialRule?.active ?? true);
  const [conditions, setConditions] = useState(initialRule?.conditions?.length ? initialRule.conditions : []);
  const [actions, setActions] = useState(
    initialRule?.actions?.length ? initialRule.actions : [emptyAction(1)]
  );
  const [isSaving, setIsSaving] = useState(false);

  const [testLeadId, setTestLeadId] = useState('');
  const [testConfirming, setTestConfirming] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testLogs, setTestLogs] = useState(null);
  const [testError, setTestError] = useState(null);

  const token = localStorage.getItem('token');
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const cleanedActions = () =>
    actions.map((a, i) => {
      const base = { actionType: a.actionType, order: i + 1 };
      if (a.actionType === 'EMAIL') {
        return { ...base, to: a.to || undefined, templateId: a.templateId || undefined, subject: a.templateId ? undefined : (a.subject || undefined), message: a.templateId ? undefined : (a.message || undefined) };
      }
      if (a.actionType === 'SMS') {
        return { ...base, to: a.to || undefined, templateId: a.templateId || undefined, message: a.templateId ? undefined : (a.message || undefined) };
      }
      if (a.actionType === 'WHATSAPP') {
        return { ...base, to: a.to || undefined, message: a.message || undefined };
      }
      if (a.actionType === 'CUSTOM_API') {
        const method = a.httpMethod || 'POST';
        const bodyAllowed = method !== 'GET';
        const bodyType = a.bodyType || 'JSON';
        const usesFormFields = bodyAllowed && (bodyType === 'FORM_URLENCODED' || bodyType === 'MULTIPART_FORM_DATA');
        const usesRawBody = bodyAllowed && !usesFormFields && bodyType !== 'NONE';
        return {
          ...base,
          httpMethod: method,
          url: a.url || '',
          headers: a.headers && Object.keys(a.headers).length ? a.headers : undefined,
          queryParams: a.queryParams && Object.keys(a.queryParams).length ? a.queryParams : undefined,
          bodyType: bodyAllowed ? bodyType : undefined,
          body: usesRawBody ? (a.body || undefined) : undefined,
          formFields: usesFormFields && a.formFields && Object.keys(a.formFields).length ? a.formFields : undefined,
        };
      }
      if (a.actionType === 'INTERNAL_NOTIFICATION') {
        return { ...base, to: a.to || undefined, message: a.message || undefined };
      }
      return base;
    });

  const validate = () => {
    if (!name.trim()) { toast.error('Give this rule a name'); return false; }
    if (actions.length === 0) { toast.error('Add at least one action'); return false; }
    for (const a of actions) {
      if (a.actionType === 'CUSTOM_API' && !a.url?.trim()) {
        toast.error('Every webhook action needs a URL');
        return false;
      }
      if (a.actionType === 'INTERNAL_NOTIFICATION' && !a.message?.trim()) {
        toast.error('Every in-app notification needs a message');
        return false;
      }
    }
    return true;
  };

  const saveRule = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        companyId,
        name: name.trim(),
        description: description.trim() || undefined,
        triggerType,
        active,
        conditions: conditions.map((c) => ({
          field: c.field.trim(),
          operator: c.operator,
          value: OPERATOR_OPTIONS.find((o) => o.value === c.operator)?.needsValue ? c.value : undefined,
        })),
        actions: cleanedActions(),
        createdById: user?.id,
        createdByName: user?.name || user?.fullName || user?.username,
      };
      if (isEdit) {
        await api.put(`/api/automation/rules/${initialRule.id}`, payload);
        toast.success('Rule updated');
      } else {
        await api.post('/api/automation/rules', payload);
        toast.success('Rule created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong saving this rule — please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const runTest = async () => {
    if (!testLeadId.trim()) {
      toast.error('Enter a lead ID to test against');
      return;
    }
    if (!isEdit) {
      toast.error('Save the rule first, then test it');
      return;
    }
    setIsTesting(true);
    setTestError(null);
    setTestLogs(null);
    try {
      const { data } = await api.post(`/api/automation/rules/${initialRule.id}/test/${testLeadId.trim()}`);
      setTestLogs(data);
    } catch (err) {
      setTestError(err.response?.data?.message || 'Test-fire failed — the lead ID may not exist, or an action errored.');
    } finally {
      setIsTesting(false);
      setTestConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div className="flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h2 className="cfg-heading text-[16px] font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FiZap className="text-[#673AB7]" size={18} /> {isEdit ? 'Edit Automation Rule' : 'New Automation Rule'}
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
            <FiX size={20} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-7">
          {/* Basics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Rule name</label>
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Welcome email on new website lead" />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Status</label>
              <button
                type="button"
                onClick={() => setActive((a) => !a)}
                className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-[13px] font-medium transition ${active ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-900 dark:border-gray-700'}`}
              >
                {active ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />} {active ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Description <span className="font-normal text-gray-400">(optional)</span></label>
            <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this rule do?" />
          </div>

          <hr className="border-gray-100 dark:border-gray-700" />

          {/* Trigger */}
          <div>
            <h3 className="cfg-heading text-[14px] font-bold text-gray-800 dark:text-gray-100 mb-2.5 flex items-center gap-1.5"><FiZap className="cfg-heading text-[#673AB7]" size={16} /> When this happens</h3>
            <select className={inputClass} value={triggerType} onChange={(e) => setTriggerType(e.target.value)}>
              {TRIGGER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <hr className="border-gray-100 dark:border-gray-700" />

          {/* Conditions */}
          <div>
            <h3 className="cfg-heading text-[14px] font-bold text-gray-800 dark:text-gray-100 mb-2.5 flex items-center gap-1.5"><FiFilter className="cfg-heading text-[#673AB7]" size={16} /> And all of these are true</h3>
            <ConditionsEditor conditions={conditions} onChange={setConditions} />
          </div>

          <hr className="border-gray-100 dark:border-gray-700" />

          {/* Actions */}
          <div>
            <h3 className="cfg-heading text-[14px] font-bold text-gray-800 dark:text-gray-100 mb-2.5 flex items-center gap-1.5"><FiList className="cfg-heading text-[#673AB7]" size={16} /> Do this, in order</h3>
            <ActionsEditor actions={actions} onChange={setActions} triggerType={triggerType} whatsappConfigured={whatsappConfigured} />
          </div>

          <hr className="border-gray-100 dark:border-gray-700" />

          {/* Test fire */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-gray-50 dark:bg-gray-900/60 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <FiPlay className="text-[#673AB7]" size={15} />
              <h3 className="cfg-heading text-[14px] font-bold text-gray-800 dark:text-gray-100">Test-fire (sends real messages)</h3>
            </div>
            <div className="p-4 bg-white dark:bg-gray-800 space-y-3">
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                This executes every action for real against the lead you pick — it does not simulate, and conditions above are skipped. Use a test/dummy lead, not a real customer.
              </p>
              {!isEdit && (
                <p className="text-[13px] text-yellow-700 dark:text-yellow-400 flex items-center gap-1.5"><FiInfo size={14} /> Save this rule first to enable testing.</p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className={inputClass}
                  placeholder="Lead ID to test against"
                  value={testLeadId}
                  onChange={(e) => setTestLeadId(e.target.value)}
                  disabled={!isEdit}
                />
                {!testConfirming ? (
                  <button
                    type="button"
                    disabled={!isEdit}
                    onClick={() => setTestConfirming(true)}
                    className="px-4 py-2 bg-gray-900 hover:bg-black dark:bg-gray-700 dark:hover:bg-gray-600 text-white text-[13px] font-medium rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-1.5 whitespace-nowrap"
                  >
                    <FiPlay size={13} /> Test-fire
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={runTest} disabled={isTesting} className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-[13px] font-medium rounded-lg transition flex items-center gap-1.5 whitespace-nowrap">
                      {isTesting ? <FiRefreshCw className="animate-spin" size={13} /> : <FiCheckCircle size={13} />} Confirm, send real messages
                    </button>
                    <button type="button" onClick={() => setTestConfirming(false)} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[13px] rounded-lg transition hover:bg-gray-200">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {testError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 rounded-lg border border-red-100 dark:border-red-800 text-xs flex items-center gap-1.5">
                  <FiAlertCircle size={16} /> {testError}
                </div>
              )}
              {testLogs && <TestResultsTable logs={testLogs} />}
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-none px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 text-[13px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition">
            Cancel
          </button>
          <button
            onClick={saveRule}
            disabled={isSaving}
            className="px-6 py-2.5 bg-[#673AB7] hover:bg-[#5e35a6] text-white text-[13px] font-medium rounded-lg transition disabled:opacity-70 flex items-center gap-2 shadow-sm"
          >
            {isSaving ? <FiRefreshCw className="animate-spin" size={15} /> : <FiSave size={15} />} {isEdit ? 'Save Changes' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Rule detail logs (recent activity for one rule)
// ============================================================================

const RuleLogsPanel = ({ rule, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorage.getItem('token');
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get(`/api/automation/logs/rule/${rule.id}`);
        setLogs(data || []);
      } catch (err) {
        toast.error('Failed to load activity for this rule');
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule.id]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div className="flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-none flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h2 className="cfg-heading text-[16px] font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FiActivity className="text-[#673AB7]" size={18} /> Activity Log — {rule.name}
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
            <FiX size={20} />
          </button>
        </div>
        
        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center py-10"><FiRefreshCw className="animate-spin text-[#673AB7] text-2xl" /></div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              <FiActivity className="mx-auto text-3xl text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-[14px] text-gray-500 dark:text-gray-400">This rule hasn't fired yet.</p>
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-left text-[13px]">
                <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="p-3 font-semibold">Lead</th>
                    <th className="p-3 font-semibold">Action</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-800">
                  {logs.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                      <td className="p-3">{l.leadName || <span className="italic text-gray-400">Unknown</span>}</td>
                      <td className="p-3">{actionLabel(l.actionType)}</td>
                      <td className="p-3"><StatusBadge status={l.status} /></td>
                      <td className="p-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{l.executedAt ? new Date(l.executedAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Rules list tab
// ============================================================================

const RulesTab = ({ companyId, user, whatsappConfigured }) => {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [builderRule, setBuilderRule] = useState(undefined); // undefined = closed, null = new, object = edit
  const [logsRule, setLogsRule] = useState(null);
  const [ruleToDelete, setRuleToDelete] = useState(null); // Added state for delete confirmation

  const token = localStorage.getItem('token');
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/api/automation/rules/company/${companyId}`);
      const sorted = [...(data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setRules(sorted);
    } catch (err) {
      toast.error('Failed to load automation rules');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { if (companyId) fetchRules(); }, [companyId, fetchRules]);

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/api/automation/rules/${rule.id}/toggle`, null, { params: { active: !rule.active } });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, active: !rule.active } : r)));
      toast.success(!rule.active ? 'Rule enabled' : 'Rule disabled');
    } catch (err) {
      toast.error('Failed to update this rule\'s status');
    }
  };

  const confirmDeleteRule = async () => {
    if (!ruleToDelete) return;
    try {
      await api.delete(`/api/automation/rules/${ruleToDelete.id}`);
      toast.success('Rule deleted');
      setRules((rs) => rs.filter((r) => r.id !== ruleToDelete.id));
    } catch (err) {
      toast.error('Failed to delete this rule');
    } finally {
      setRuleToDelete(null);
    }
  };

  const filtered = rules.filter((r) => r.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 animate-fade-in relative">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="cfg-heading text-lg font-bold text-gray-800 dark:text-white mb-1.5">Automation rules</h2>
          <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-xl">
            When something happens to a lead, run these steps automatically — send a message, notify a teammate, or call your own webhook.
          </p>
        </div>
        <button
          onClick={() => setBuilderRule(null)}
          className="px-5 py-2.5 bg-[#673AB7] hover:bg-[#5e35a6] text-white text-[13px] font-medium rounded-lg transition flex items-center justify-center gap-2 shadow-sm whitespace-nowrap"
        >
          <FiPlusCircle size={16} /> New Rule
        </button>
      </div>

      <div className="relative mb-4 max-w-xs">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
        <input className={`${inputClass} pl-9`} placeholder="Search rules by name" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-14"><FiRefreshCw className="animate-spin text-[#673AB7] text-2xl" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <FiZap className="mx-auto text-3xl text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-[14px] text-gray-500 dark:text-gray-400">
            {rules.length === 0 ? 'No automation rules yet — create your first one.' : 'No rules match your search.'}
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-[13px]">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-4 font-semibold">Rule Name</th>
                  <th className="p-4 font-semibold">Trigger</th>
                  <th className="p-4 font-semibold text-center">Actions</th>
                  <th className="p-4 font-semibold text-center">Status</th>
                  <th className="p-4 font-semibold text-right">Manage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.map((rule) => (
                  <tr key={rule.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="p-4">
                      <p className="font-semibold text-gray-800 dark:text-gray-100 text-[14px]">{rule.name}</p>
                      {rule.description && <p className="text-[12px] text-gray-400 mt-1 max-w-sm truncate">{rule.description}</p>}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{triggerLabel(rule.triggerType)}</td>
                    <td className="p-4 text-center text-gray-600 dark:text-gray-300 font-medium">{rule.actions?.length || 0}</td>
                    <td className="p-4 text-center">
                      <button onClick={() => toggleActive(rule)} className="inline-flex items-center gap-1.5 text-[12px] font-medium">
                        {rule.active ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"><FiToggleRight size={14} /> Active</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600"><FiToggleLeft size={14} /> Inactive</span>
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => setLogsRule(rule)} title="View activity" className="p-2 text-gray-400 hover:text-[#673AB7] hover:bg-[#F3E8FF] dark:hover:bg-purple-900/20 rounded-lg transition">
                          <FiActivity size={16} />
                        </button>
                        <button onClick={() => setBuilderRule(rule)} title="Edit rule" className="p-2 text-gray-400 hover:text-[#673AB7] hover:bg-[#F3E8FF] dark:hover:bg-purple-900/20 rounded-lg transition">
                          <FiEdit2 size={16} />
                        </button>
                        <button onClick={() => setRuleToDelete(rule)} title="Delete rule" className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Popups */}
      {builderRule !== undefined && (
        <RuleBuilder
          initialRule={builderRule}
          companyId={companyId}
          user={user}
          whatsappConfigured={whatsappConfigured}
          onClose={() => setBuilderRule(undefined)}
          onSaved={() => { setBuilderRule(undefined); fetchRules(); }}
        />
      )}
      
      {logsRule && <RuleLogsPanel rule={logsRule} onClose={() => setLogsRule(null)} />}

      {/* Delete Confirmation Modal */}
      {ruleToDelete && (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center relative overflow-hidden">
            <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-5">
              <FiTrash2 size={26} />
            </div>
            <h3 className="cfg-heading text-[18px] font-bold text-gray-900 dark:text-white mb-2">Delete Automation Rule?</h3>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-800 dark:text-gray-200">"{ruleToDelete.name}"</span>? This action cannot be undone, though past activity logs will remain visible in the Activity tab.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button 
                onClick={() => setRuleToDelete(null)} 
                className="px-5 py-2.5 text-[14px] font-medium text-gray-600 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition w-full"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteRule} 
                className="px-5 py-2.5 text-[14px] font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition shadow-sm w-full"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Activity log tab (company-wide, paginated + client-side filters)
// ============================================================================

const ActivityTab = ({ companyId }) => {
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [triggerFilter, setTriggerFilter] = useState('ALL');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [nameFilter, setNameFilter] = useState('');

  const token = localStorage.getItem('token');
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const fetchLogs = useCallback(async (p) => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/api/automation/logs/company/${companyId}`, { params: { page: p, size: 50 } });
      setPageData(data);
    } catch (err) {
      toast.error('Failed to load the activity log');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { if (companyId) fetchLogs(page); }, [companyId, page, fetchLogs]);

  const rows = (pageData?.content || []).filter((l) => {
    if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
    if (triggerFilter !== 'ALL' && l.triggerType !== triggerFilter) return false;
    if (actionFilter !== 'ALL' && l.actionType !== actionFilter) return false;
    if (nameFilter && !l.ruleName?.toLowerCase().includes(nameFilter.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 animate-fade-in">
      <div className="mb-5">
        <h2 className="cfg-heading text-lg font-bold text-gray-800 dark:text-white mb-1.5">Automation activity</h2>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 max-w-2xl">
          Every action your rules have run, newest first. Filters below apply to the loaded page only.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
        <input className={inputClass} placeholder="Filter by rule name" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} />
        <select className={inputClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All statuses</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
        </select>
        <select className={inputClass} value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)}>
          <option value="ALL">All triggers</option>
          {TRIGGER_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select className={inputClass} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          <option value="ALL">All action types</option>
          {ACTION_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-14"><FiRefreshCw className="animate-spin text-[#673AB7] text-2xl" /></div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <FiActivity className="mx-auto text-3xl text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-[13px] text-gray-500 dark:text-gray-400">No activity matches these filters yet.</p>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-[13px]">
              <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-xs">
                <tr>
                  <th className="p-3 font-semibold">Rule</th>
                  <th className="p-3 font-semibold">Lead</th>
                  <th className="p-3 font-semibold">Trigger</th>
                  <th className="p-3 font-semibold">Action</th>
                  <th className="p-3 font-semibold">Status</th>
                  <th className="p-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((l) => (
                  <tr key={l.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="p-3 font-medium text-gray-800 dark:text-gray-100">{l.ruleName || <span className="italic text-gray-400 font-normal">Rule deleted</span>}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300">{l.leadName || <span className="italic text-gray-400">—</span>}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300">{triggerLabel(l.triggerType)}</td>
                    <td className="p-3 text-gray-600 dark:text-gray-300">{actionLabel(l.actionType)}</td>
                    <td className="p-3"><StatusBadge status={l.status} /></td>
                    <td className="p-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{l.executedAt ? new Date(l.executedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pageData && (
            <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-[13px] text-gray-500 dark:text-gray-400">
              <span>Page {pageData.number + 1} of {Math.max(pageData.totalPages, 1)} · {pageData.totalElements} total</span>
              <div className="flex items-center gap-2">
                <button
                  disabled={pageData.first}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:border-[#673AB7] hover:text-[#673AB7] transition bg-white dark:bg-gray-800"
                >
                  <FiChevronLeft size={16} />
                </button>
                <button
                  disabled={pageData.last}
                  onClick={() => setPage((p) => p + 1)}
                  className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:border-[#673AB7] hover:text-[#673AB7] transition bg-white dark:bg-gray-800"
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Top-level page
// ============================================================================

const WorkflowAutomation = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('rules');

  const isCompanyOwner = user?.accountType === "COMPANY";
  const companyId = isCompanyOwner ? user?.id : user?.companyId;

  // Best-effort hint only — the backend is the source of truth on whether
  // WhatsApp is actually usable; this just avoids surfacing a scary warning
  // if we simply don't know one way or the other.
  const whatsappConfigured = typeof user?.whatsappConfigured === 'boolean' ? user.whatsappConfigured : undefined;

  return (
    <div className="p-4 md:p-6 bg-[#FAF8FF] shadow-inner dark:bg-gray-900 dark:text-white min-h-screen cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#673AB7]/10 rounded-xl">
            <FiZap className="text-xl text-[#673AB7]" />
          </div>
          <div>
            <h1 className="text-xl font-bold cfg-heading text-gray-800 dark:text-white">Workflow Automation</h1>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">Trigger emails, SMS, WhatsApp, webhooks, and in-app alerts automatically from lead activity.</p>
          </div>
        </div>
      </div>

      {!companyId && (
        <div className="mb-5 p-3 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 text-[13px] flex items-start gap-2 shadow-sm">
          <FiAlertCircle className="mt-0.5 text-base" />
          <p>Could not determine your company — please refresh, or contact support if this continues.</p>
        </div>
      )}

      <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 mb-6 scrollbar-hide">
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-5 py-3 text-[14px] font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'rules' ? 'border-[#673AB7] text-[#673AB7]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          <FiSliders size={15} /> Rules
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-5 py-3 text-[14px] font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'activity' ? 'border-[#673AB7] text-[#673AB7]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          <FiActivity size={15} /> Activity Log
        </button>
      </div>

      {companyId && activeTab === 'rules' && <RulesTab companyId={companyId} user={user} whatsappConfigured={whatsappConfigured} />}
      {companyId && activeTab === 'activity' && <ActivityTab companyId={companyId} />}
    </div>
  );
};

export default WorkflowAutomation;
